import type { ComplianceOverviewData } from '../../../services/LicenseQueryService';
import { COMPLIANCE_ATTENTION_LIST_META } from './client-status';
import {
	COMPLIANCE_ATTENTION_LIST_IDS,
	type ComplianceAttentionListId,
} from './list-ids';

/**
 * PD-16306 — the Compliance dashboard's Needs Attention cards, defined once:
 * what each is called, where its Review link goes, and which figure of the
 * dashboard payload it shows.
 *
 * WHY. Two surfaces tell a customer about these cards: the dashboard itself
 * and the weekly compliance summary email. The daily digest the email
 * replaces kept its own queries and its own links, and every one of its
 * figures disagreed with the dashboard (it would have told Pharmsource
 * 66,186 matches were waiting for review). Reading the title, the link and
 * the count from one table means the email can only ever repeat what the
 * dashboard says.
 */

/** The dashboard page itself. */
export const COMPLIANCE_DASHBOARD_PATH = '/compliance/dashboard';

/**
 * The Found License Fills a Gap list. It lives on the Matching page, not
 * among the Needs Attention lists, because a found license is a suggestion
 * rather than a license on file (PD-16256 / PD-16467).
 */
export const LICENSE_MATCH_FILLS_GAP_LIST_ID = 'license-match-fills-gap';

/**
 * A Needs Attention list's URL, optionally narrowed by one filter
 * (`<filterKey>:<value>`). No Customer filter: every attention list locks the
 * active-buyer filter, which already implies it.
 */
export function complianceAttentionListPath(
	listId: ComplianceAttentionListId,
	filter?: string,
): string {
	const base = `/compliance/attention?list=${listId}`;
	return filter != null ? `${base}&f=${encodeURIComponent(filter)}` : base;
}

/** The dashboard's two card groups, in order. */
export type ComplianceAttentionCardGroup = 'fixNow' | 'improve';

export const COMPLIANCE_ATTENTION_CARD_GROUPS: ReadonlyArray<{
	group: ComplianceAttentionCardGroup;
	label: string;
}> = [
	{ group: 'fixNow', label: 'Fix Now' },
	{ group: 'improve', label: 'One Click to Improve' },
];

/** A card is one of the attention lists, or Found License Fills a Gap. */
export type ComplianceAttentionCardId = ComplianceAttentionListId | 'fillsGap';

/** The part of the dashboard payload the cards read. */
export type ComplianceAttentionFiguresModel =
	ComplianceOverviewData['attention'];

export interface ComplianceAttentionCardModel {
	id: ComplianceAttentionCardId;
	/** The card's title on the dashboard. */
	title: string;
	group: ComplianceAttentionCardGroup;
	/** Where the card's Review link goes, as an app path. */
	href: string;
	/**
	 * The card's figure: ACCOUNTS (active buyers), like every Needs Attention
	 * card. Null only for Number Not Found when the server could not read the
	 * lookups; the card then says it could not check, because a zero would
	 * claim every number on file is good. A figure an older cached payload
	 * omits reads 0, as it always has on the dashboard.
	 */
	count: (attention: ComplianceAttentionFiguresModel) => number | null;
}

/** Which dashboard figure each list-backed card shows. */
const LIST_CARD_COUNTS: Record<
	Exclude<
		ComplianceAttentionListId,
		// No card of their own: Recently Fixed is not a problem, and the two
		// Adverse Action views are figures on the Adverse Action card
		// (PD-16615).
		| 'compliance-recently-fixed'
		| 'compliance-adverse-flag-only'
		| 'compliance-adverse-reviewed'
	>,
	ComplianceAttentionCardModel['count']
> = {
	[COMPLIANCE_ATTENTION_LIST_IDS.controlledNoDea]: (a) =>
		a.controlledNoDea ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.deadLicense]: (a) => a.deadLicense,
	[COMPLIANCE_ATTENTION_LIST_IDS.adverseAction]: (a) => a.adverseAction ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.wrongNumber]: (a) => a.wrongNumber ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.failedLookup]: (a) => a.failedLookup ?? null,
	[COMPLIANCE_ATTENTION_LIST_IDS.expiringSoon]: (a) => a.expiringSoon,
	[COMPLIANCE_ATTENTION_LIST_IDS.noVerifiedLicense]: (a) =>
		a.noActiveLicense.total,
	[COMPLIANCE_ATTENTION_LIST_IDS.linkEnded]: (a) => a.linkEnded,
	[COMPLIANCE_ATTENTION_LIST_IDS.readyToConfirm]: (a) => a.unconfirmedMatch,
	[COMPLIANCE_ATTENTION_LIST_IDS.replacementAvailable]: (a) =>
		a.replacementAvailable ?? 0,
};

/** The One Click cards; every other list-backed card is Fix Now. */
const IMPROVE_LISTS: ReadonlySet<string> = new Set([
	COMPLIANCE_ATTENTION_LIST_IDS.readyToConfirm,
	COMPLIANCE_ATTENTION_LIST_IDS.replacementAvailable,
]);

/**
 * Every Needs Attention card in dashboard order: the list-backed cards in
 * COMPLIANCE_ATTENTION_LIST_META order (Fix Now, then One Click), then Found
 * License Fills a Gap. Titles come from the list meta, so a card, the client
 * badge and the email name each problem the same way.
 */
export const COMPLIANCE_ATTENTION_CARDS: ReadonlyArray<ComplianceAttentionCardModel> =
	[
		...COMPLIANCE_ATTENTION_LIST_META.map(
			(meta): ComplianceAttentionCardModel => {
				// Recently Fixed has no card; a list added to the meta without
				// a figure here fails loudly at load rather than showing 0.
				const count: ComplianceAttentionCardModel['count'] | undefined = (
					LIST_CARD_COUNTS as Partial<
						Record<string, ComplianceAttentionCardModel['count']>
					>
				)[meta.listId];
				if (count == null) {
					throw new Error(`No dashboard figure for ${meta.listId}`);
				}
				return {
					id: meta.listId,
					title: meta.title,
					group: IMPROVE_LISTS.has(meta.listId) ? 'improve' : 'fixNow',
					href: complianceAttentionListPath(meta.listId),
					count,
				};
			},
		),
		{
			id: 'fillsGap',
			title: 'Found License Fills a Gap',
			group: 'improve',
			href: `/licenses/matching?list=${LICENSE_MATCH_FILLS_GAP_LIST_ID}`,
			count: (a) => a.fillsGap ?? 0,
		},
	];

/** One card by id. Throws for an id that is not a card. */
export function complianceAttentionCard(
	id: ComplianceAttentionCardId,
): ComplianceAttentionCardModel {
	const card = COMPLIANCE_ATTENTION_CARDS.find((entry) => entry.id === id);
	if (card == null) {
		throw new Error(`No compliance attention card ${id}`);
	}
	return card;
}
